import { Text } from '@figma/fpl-components';
import { Icon24Image, Icon24Transform3dLarge } from '@figma/fpl-icons';
import { color, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { useInView } from 'motion/react';
import { useRef, useState } from 'react';
import { GalleryFileViewer } from '@/components/Common/ImageList/GalleryFileViewer';
import LazyThreeDeeViewer from '@/components/Nodes/LazyThreeDeeViewer';
import { useResolvedAssets } from '@/state/workflow.state';
import { assetUrl, assetViewUrl, thumbnailSource } from '@/utils/assetUrls';
import type { DesignAppResult } from '@/types/design-app.types';

export function ToolOutputCard({
  result,
  suspendMediaPlayback = false,
}: {
  result: DesignAppResult;
  suspendMediaPlayback?: boolean;
}) {
  const resolvedAssets = useResolvedAssets();
  const modelPreviewRef = useRef<HTMLDivElement | null>(null);
  const isModelInView = useInView(modelPreviewRef);
  const [loadedModelUrl, setLoadedModelUrl] = useState<string>();

  switch (result.type) {
    case 'image': {
      const url = assetViewUrl(result, resolvedAssets);
      if (!url) {
        break;
      }
      return (
        <img
          src={url}
          alt={result.name}
          {...stylex.props(styles.image)}
          style={{
            aspectRatio: result.width && result.height ? `${result.width}/${result.height}` : undefined,
          }}
        />
      );
    }
    case 'video': {
      return (
        <div
          {...stylex.props(styles.media)}
          style={{
            aspectRatio: result.width && result.height ? `${result.width}/${result.height}` : undefined,
          }}
        >
          <GalleryFileViewer
            container="design-app"
            file={result}
            index={0}
            selected={0}
            suspendMediaPlayback={suspendMediaPlayback}
          />
        </div>
      );
    }
    case 'audio': {
      return (
        <div {...stylex.props(styles.paddedContent)}>
          <GalleryFileViewer
            audioClassName={stylex.props(styles.media).className}
            container="design-app"
            file={result}
            index={0}
            selected={0}
            suspendMediaPlayback={suspendMediaPlayback}
          />
        </div>
      );
    }
    case '3D': {
      const source = thumbnailSource(result, resolvedAssets);
      const url = assetUrl(result, resolvedAssets);
      const showModel = Boolean(url) && isModelInView;
      return (
        <div
          ref={modelPreviewRef}
          className={`${stylex.props(styles.model).className} media-container-dark`}
          style={{
            aspectRatio: result.width && result.height ? `${result.width}/${result.height}` : '1',
          }}
        >
          {url && showModel && (
            <LazyThreeDeeViewer
              objUrl={url}
              mime={result.mime}
              allowInteraction
              displayBackground
              responsive
              onModelLoaded={setLoadedModelUrl}
            />
          )}
          {source.kind === 'image'
            ? (!showModel || loadedModelUrl !== url) && (
                <img src={source.src} alt={result.name} {...stylex.props(styles.modelPoster)} />
              )
            : !showModel && (
                <div {...stylex.props(styles.placeholder, styles.modelPoster)}>
                  <Icon24Transform3dLarge />
                </div>
              )}
          <div aria-hidden="true" {...stylex.props(styles.modelBadge)}>
            <Icon24Transform3dLarge />
          </div>
        </div>
      );
    }
    case 'text':
      return (
        <div {...stylex.props(styles.paddedContent, styles.text)}>
          <Text.div variant="body-large" {...stylex.props(styles.textContent)}>
            {result.value}
          </Text.div>
        </div>
      );
  }

  return (
    <div {...stylex.props(styles.placeholder, styles.missingImage)}>
      <Icon24Image />
    </div>
  );
}

const styles = stylex.create({
  media: {
    width: '100%',
  },
  image: {
    display: 'block',
    width: '100%',
    height: 'auto',
    minHeight: 48,
    objectFit: 'contain',
  },
  paddedContent: {
    boxSizing: 'border-box',
    width: '100%',
    paddingBlock: 48,
    paddingInline: spacer.spacer24px,
  },
  model: {
    position: 'relative',
    width: '100%',
  },
  modelPoster: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    pointerEvents: 'none',
  },
  modelBadge: {
    '--fpl-icon-color': '#fff',
    position: 'absolute',
    left: spacer.spacer12px,
    bottom: spacer.spacer12px,
    pointerEvents: 'none',
  },
  text: {
    backgroundColor: color.bg,
  },
  textContent: {
    color: color.text,
    textAlign: 'left',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  placeholder: {
    '--fpl-icon-color': color.iconSecondary,
    display: 'grid',
    placeItems: 'center',
    backgroundColor: color.bgSecondary,
  },
  missingImage: {
    height: 180,
  },
});
